/**
 * Frontend permission constants — values must exactly match the backend
 * PermissionCatalog seed data (format: ACTION_MODULE_ENTITY).
 *
 * Keep in sync with:
 *   backend/myLedger.Shared/Constants/Permissions.cs
 *   backend/myLedger.Persistence/SeedData/PermissionCatalog.cs
 */
export const Permissions = {
  // ── General Ledger — Journal ───────────────────────────────────────────────
  GL_JOURNAL_VIEW: 'VIEW_GL_JOURNAL',
  GL_JOURNAL_CREATE: 'CREATE_GL_JOURNAL',
  GL_JOURNAL_EDIT: 'EDIT_GL_JOURNAL',
  GL_JOURNAL_DELETE: 'DELETE_GL_JOURNAL',
  GL_JOURNAL_SUBMIT:  'SUBMIT_GL_JOURNAL',
  GL_JOURNAL_APPROVE: 'APPROVE_GL_JOURNAL',
  GL_JOURNAL_REJECT:  'REJECT_GL_JOURNAL',
  GL_JOURNAL_POST:    'POST_GL_JOURNAL',
  GL_JOURNAL_REVERSE: 'REVERSE_GL_JOURNAL',
  GL_JOURNAL_CANCEL:  'CANCEL_GL_JOURNAL',
  GL_JOURNAL_PRINT:   'PRINT_GL_JOURNAL',
  GL_JOURNAL_EXPORT:  'EXPORT_GL_JOURNAL',

  // ── General Ledger — Financial Period ──────────────────────────────────────
  GL_PERIOD_VIEW: 'VIEW_GL_PERIOD',
  GL_PERIOD_CLOSE: 'CLOSE_GL_PERIOD',
  GL_PERIOD_REOPEN: 'REOPEN_GL_PERIOD',

  // ── General Ledger — Chart of Accounts ────────────────────────────────────
  GL_COA_VIEW:   'VIEW_GL_COA',
  GL_COA_CREATE: 'CREATE_GL_COA',
  GL_COA_EDIT:   'EDIT_GL_COA',
  GL_COA_DELETE: 'DELETE_GL_COA',
  GL_COA_PRINT:  'PRINT_GL_COA',
  GL_COA_EXPORT: 'EXPORT_GL_COA',

  // ── Reports ────────────────────────────────────────────────────────────────
  RPT_TRIAL_BALANCE: 'VIEW_RPT_TRIALBALANCE',
  RPT_PL_STATEMENT: 'VIEW_RPT_PLSTATEMENT',
  RPT_BALANCE_SHEET: 'VIEW_RPT_BALANCESHEET',
  RPT_CASH_FLOW: 'VIEW_RPT_CASHFLOW',
  RPT_LEDGER: 'VIEW_RPT_LEDGER',
  RPT_AGEING: 'VIEW_RPT_AGEING',

  // ── Reporting centre ───────────────────────────────────────────────────────
  RPT_EXPORT_DATA:         'EXPORT_RPT_DATA',
  RPT_PRINT_DATA:          'PRINT_RPT_DATA',
  RPT_MANAGE_SHARED_VIEWS: 'MANAGE_RPT_SHAREDVIEWS',
  RPT_SENSITIVE:           'VIEW_RPT_SENSITIVE',
  RPT_RECON:               'VIEW_RPT_RECON',
  RPT_AUDIT_TRAIL:         'VIEW_RPT_AUDITTRAIL',
  RPT_USER_ACTIVITY:       'VIEW_RPT_USERACTIVITY',
  RPT_PERIOD_STATUS:       'VIEW_RPT_PERIODSTATUS',
  RPT_SOURCE_TRACE:        'VIEW_RPT_SOURCETRACE',
  RPT_CURRENCY:            'VIEW_RPT_CURRENCY',
  RPT_FS_MAPPING:          'VIEW_RPT_FSMAPPING',

  // ── Management analytics ────────────────────────────────────────────────
  RPT_EXEC_DASHBOARD:   'VIEW_RPT_EXECDASHBOARD',
  RPT_PROFITABILITY:    'VIEW_RPT_PROFITABILITY',
  RPT_EXPENSE_ANALYSIS: 'VIEW_RPT_EXPENSEANALYSIS',
  RPT_COST_CENTRE_PERF: 'VIEW_RPT_COSTCENTREPERF',
  RPT_FINANCIAL_TREND:  'VIEW_RPT_FINANCIALTREND',
  RPT_ACCOUNT_ACTIVITY: 'VIEW_RPT_ACCOUNTACTIVITY',
  RPT_JOURNAL_ANALYSIS: 'VIEW_RPT_JOURNALANALYSIS',
  RPT_SCORECARD:        'VIEW_RPT_SCORECARD',

  // ── Administration — Users ─────────────────────────────────────────────────
  ADMIN_USER_VIEW: 'VIEW_ADMIN_USER',
  ADMIN_USER_CREATE: 'CREATE_ADMIN_USER',
  ADMIN_USER_EDIT: 'EDIT_ADMIN_USER',
  ADMIN_USER_DELETE: 'DELETE_ADMIN_USER',
  ADMIN_USER_RESET_PW: 'RESETPW_ADMIN_USER',
  ADMIN_USER_LOCK:    'LOCK_ADMIN_USER',
  ADMIN_USER_DISABLE: 'DISABLE_ADMIN_USER',
  ADMIN_USER_ASSIGN_ROLES: 'ASSIGN_ROLES_ADMIN_USER',

  // ── Administration — Password Policy ──────────────────────────────────────
  ADMIN_PASSWORD_POLICY_VIEW: 'VIEW_ADMIN_PASSWORDPOLICY',
  ADMIN_PASSWORD_POLICY_EDIT: 'EDIT_ADMIN_PASSWORDPOLICY',

  // ── Administration — Session Management ───────────────────────────────────
  SYSTEM_LOG_VIEW:         'VIEW_SYS_LOG',

  EMAIL_SETTINGS_VIEW:     'VIEW_CFG_EMAILSETTING',
  EMAIL_SETTINGS_MANAGE:   'MANAGE_CFG_EMAILSETTING',

  ADMIN_SESSION_VIEW:      'VIEW_ADMIN_SESSION',
  ADMIN_SESSION_TERMINATE: 'TERMINATE_ADMIN_SESSION',

  // ── Security Audit ─────────────────────────────────────────────────────────
  SECURITY_AUDIT_VIEW:   'VIEW_SECURITY_AUDIT',
  SECURITY_AUDIT_EXPORT: 'EXPORT_SECURITY_AUDIT',

  // ── Administration — Roles ─────────────────────────────────────────────────
  ADMIN_ROLE_VIEW: 'VIEW_ADMIN_ROLE',
  ADMIN_ROLE_CREATE: 'CREATE_ADMIN_ROLE',
  ADMIN_ROLE_EDIT: 'EDIT_ADMIN_ROLE',
  ADMIN_ROLE_DELETE: 'DELETE_ADMIN_ROLE',

  // ── Administration — Company ───────────────────────────────────────────────
  ADMIN_COMPANY_VIEW:   'VIEW_ADMIN_COMPANY',
  ADMIN_COMPANY_CREATE: 'CREATE_ADMIN_COMPANY',
  ADMIN_COMPANY_EDIT:   'EDIT_ADMIN_COMPANY',
  ADMIN_COMPANY_DELETE: 'DELETE_ADMIN_COMPANY',

  // ── Administration — Sample data ───────────────────────────────────────────
  SAMPLE_DATA_VIEW:     'VIEW_SYS_SAMPLEDATA',
  SAMPLE_DATA_GENERATE: 'GENERATE_SYS_SAMPLEDATA',

  // ── Administration — Misc ──────────────────────────────────────────────────
  ADMIN_PERMISSION_VIEW: 'VIEW_ADMIN_PERMISSION',
  ADMIN_AUDIT_VIEW: 'VIEW_ADMIN_AUDITLOG',

  // ── Master Data ────────────────────────────────────────────────────────────
  MST_CURRENCY_VIEW:   'VIEW_MST_CURRENCY',
  MST_CURRENCY_CREATE: 'CREATE_MST_CURRENCY',
  MST_CURRENCY_EDIT:   'EDIT_MST_CURRENCY',
  MST_CURRENCY_DELETE: 'DELETE_MST_CURRENCY',

  // ── Masters — Financial Year ───────────────────────────────────────────────
  MST_FINANCIAL_YEAR_VIEW:   'VIEW_MST_FINANCIALYEAR',
  MST_FINANCIAL_YEAR_CREATE: 'CREATE_MST_FINANCIALYEAR',
  MST_FINANCIAL_YEAR_EDIT:   'EDIT_MST_FINANCIALYEAR',
  MST_FINANCIAL_YEAR_DELETE: 'DELETE_MST_FINANCIALYEAR',
  MST_FINANCIAL_YEAR_PRINT:  'PRINT_MST_FINANCIALYEAR',
  MST_FINANCIAL_YEAR_CLOSE:  'CLOSE_MST_FINANCIALYEAR',
  MST_FINANCIAL_YEAR_REOPEN: 'REOPEN_MST_FINANCIALYEAR',

  // ── Masters — Financial Period ─────────────────────────────────────────────
  MST_FINANCIAL_PERIOD_VIEW:   'VIEW_MST_FINANCIALPERIOD',
  MST_FINANCIAL_PERIOD_CREATE: 'CREATE_MST_FINANCIALPERIOD',
  MST_FINANCIAL_PERIOD_EDIT:   'EDIT_MST_FINANCIALPERIOD',
  MST_FINANCIAL_PERIOD_DELETE: 'DELETE_MST_FINANCIALPERIOD',
  MST_FINANCIAL_PERIOD_PRINT:  'PRINT_MST_FINANCIALPERIOD',
  MST_FINANCIAL_PERIOD_CLOSE:  'CLOSE_MST_FINANCIALPERIOD',
  MST_FINANCIAL_PERIOD_REOPEN: 'REOPEN_MST_FINANCIALPERIOD',

  // ── Masters — Location ─────────────────────────────────────────────────────
  MST_LOCATION_VIEW:   'VIEW_MST_LOCATION',
  MST_LOCATION_CREATE: 'CREATE_MST_LOCATION',
  MST_LOCATION_EDIT:   'EDIT_MST_LOCATION',
  MST_LOCATION_DELETE: 'DELETE_MST_LOCATION',
  MST_LOCATION_PRINT:  'PRINT_MST_LOCATION',

  // ── Masters — Cost Center ──────────────────────────────────────────────────
  MST_COST_CENTER_VIEW:   'VIEW_MST_COSTCENTER',
  MST_COST_CENTER_CREATE: 'CREATE_MST_COSTCENTER',
  MST_COST_CENTER_EDIT:   'EDIT_MST_COSTCENTER',
  MST_COST_CENTER_DELETE: 'DELETE_MST_COSTCENTER',
  MST_COST_CENTER_PRINT:  'PRINT_MST_COSTCENTER',

  // ── Masters — Exchange Rate ────────────────────────────────────────────────
  MST_EXCHANGE_RATE_VIEW:   'VIEW_MST_EXCHANGERATE',
  MST_EXCHANGE_RATE_CREATE: 'CREATE_MST_EXCHANGERATE',
  MST_EXCHANGE_RATE_EDIT:   'EDIT_MST_EXCHANGERATE',
  MST_EXCHANGE_RATE_DELETE: 'DELETE_MST_EXCHANGERATE',
  MST_EXCHANGE_RATE_PRINT:  'PRINT_MST_EXCHANGERATE',

  // ── Masters — Bank ────────────────────────────────────────────────────────
  MST_BANK_VIEW:   'VIEW_MST_BANK',
  MST_BANK_CREATE: 'CREATE_MST_BANK',
  MST_BANK_EDIT:   'EDIT_MST_BANK',
  MST_BANK_DELETE: 'DELETE_MST_BANK',
  MST_BANK_PRINT:  'PRINT_MST_BANK',

  // ── Masters — Bank Branch ─────────────────────────────────────────────────
  MST_BANK_BRANCH_VIEW:   'VIEW_MST_BANKBRANCH',
  MST_BANK_BRANCH_CREATE: 'CREATE_MST_BANKBRANCH',
  MST_BANK_BRANCH_EDIT:   'EDIT_MST_BANKBRANCH',
  MST_BANK_BRANCH_DELETE: 'DELETE_MST_BANKBRANCH',
  MST_BANK_BRANCH_PRINT:  'PRINT_MST_BANKBRANCH',

  // ── Banking — Bank Account ─────────────────────────────────────────────────
  BNK_BANK_ACCOUNT_VIEW:   'VIEW_BNK_BANKACCOUNT',
  BNK_BANK_ACCOUNT_CREATE: 'CREATE_BNK_BANKACCOUNT',
  BNK_BANK_ACCOUNT_EDIT:   'EDIT_BNK_BANKACCOUNT',
  BNK_BANK_ACCOUNT_DELETE: 'DELETE_BNK_BANKACCOUNT',
  BNK_BANK_ACCOUNT_PRINT:  'PRINT_BNK_BANKACCOUNT',

  // ── Banking Transactions — Transfer ───────────────────────────────────────
  BNK_TRANSFER_VIEW:    'VIEW_BNK_TRANSFER',
  BNK_TRANSFER_CREATE:  'CREATE_BNK_TRANSFER',
  BNK_TRANSFER_EDIT:    'EDIT_BNK_TRANSFER',
  BNK_TRANSFER_SUBMIT:  'SUBMIT_BNK_TRANSFER',
  BNK_TRANSFER_APPROVE: 'APPROVE_BNK_TRANSFER',
  BNK_TRANSFER_REJECT:  'REJECT_BNK_TRANSFER',
  BNK_TRANSFER_DELETE:  'DELETE_BNK_TRANSFER',
  BNK_TRANSFER_POST:    'POST_BNK_TRANSFER',
  BNK_TRANSFER_REVERSE: 'REVERSE_BNK_TRANSFER',
  BNK_TRANSFER_CANCEL:  'CANCEL_BNK_TRANSFER',
  BNK_TRANSFER_PRINT:   'PRINT_BNK_TRANSFER',

  // ── Banking Transactions — Deposit ────────────────────────────────────────
  BNK_DEPOSIT_VIEW:    'VIEW_BNK_DEPOSIT',
  BNK_DEPOSIT_CREATE:  'CREATE_BNK_DEPOSIT',
  BNK_DEPOSIT_EDIT:    'EDIT_BNK_DEPOSIT',
  BNK_DEPOSIT_SUBMIT:  'SUBMIT_BNK_DEPOSIT',
  BNK_DEPOSIT_APPROVE: 'APPROVE_BNK_DEPOSIT',
  BNK_DEPOSIT_REJECT:  'REJECT_BNK_DEPOSIT',
  BNK_DEPOSIT_DELETE:  'DELETE_BNK_DEPOSIT',
  BNK_DEPOSIT_POST:    'POST_BNK_DEPOSIT',
  BNK_DEPOSIT_REVERSE: 'REVERSE_BNK_DEPOSIT',
  BNK_DEPOSIT_CANCEL:  'CANCEL_BNK_DEPOSIT',
  BNK_DEPOSIT_PRINT:   'PRINT_BNK_DEPOSIT',

  // ── Banking Transactions — Withdrawal ─────────────────────────────────────
  BNK_WITHDRAWAL_VIEW:    'VIEW_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_CREATE:  'CREATE_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_EDIT:    'EDIT_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_SUBMIT:  'SUBMIT_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_APPROVE: 'APPROVE_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_REJECT:  'REJECT_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_DELETE:  'DELETE_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_POST:    'POST_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_REVERSE: 'REVERSE_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_CANCEL:  'CANCEL_BNK_WITHDRAWAL',
  BNK_WITHDRAWAL_PRINT:   'PRINT_BNK_WITHDRAWAL',

  // ── Banking Transactions — Bank Charge ────────────────────────────────────
  BNK_CHARGE_VIEW:    'VIEW_BNK_CHARGE',
  BNK_CHARGE_CREATE:  'CREATE_BNK_CHARGE',
  BNK_CHARGE_EDIT:    'EDIT_BNK_CHARGE',
  BNK_CHARGE_SUBMIT:  'SUBMIT_BNK_CHARGE',
  BNK_CHARGE_APPROVE: 'APPROVE_BNK_CHARGE',
  BNK_CHARGE_REJECT:  'REJECT_BNK_CHARGE',
  BNK_CHARGE_DELETE:  'DELETE_BNK_CHARGE',
  BNK_CHARGE_POST:    'POST_BNK_CHARGE',
  BNK_CHARGE_REVERSE: 'REVERSE_BNK_CHARGE',
  BNK_CHARGE_CANCEL:  'CANCEL_BNK_CHARGE',
  BNK_CHARGE_PRINT:   'PRINT_BNK_CHARGE',

  // ── Banking Transactions — Bank Interest ──────────────────────────────────
  BNK_INTEREST_VIEW:    'VIEW_BNK_INTEREST',
  BNK_INTEREST_CREATE:  'CREATE_BNK_INTEREST',
  BNK_INTEREST_EDIT:    'EDIT_BNK_INTEREST',
  BNK_INTEREST_SUBMIT:  'SUBMIT_BNK_INTEREST',
  BNK_INTEREST_APPROVE: 'APPROVE_BNK_INTEREST',
  BNK_INTEREST_REJECT:  'REJECT_BNK_INTEREST',
  BNK_INTEREST_DELETE:  'DELETE_BNK_INTEREST',
  BNK_INTEREST_POST:    'POST_BNK_INTEREST',
  BNK_INTEREST_REVERSE: 'REVERSE_BNK_INTEREST',
  BNK_INTEREST_CANCEL:  'CANCEL_BNK_INTEREST',
  BNK_INTEREST_PRINT:   'PRINT_BNK_INTEREST',

  // ── Banking Transactions — Cheque ─────────────────────────────────────────
  BNK_CHEQUE_VIEW:    'VIEW_BNK_CHEQUE',
  BNK_CHEQUE_CREATE:  'CREATE_BNK_CHEQUE',
  BNK_CHEQUE_EDIT:    'EDIT_BNK_CHEQUE',
  BNK_CHEQUE_DEPOSIT: 'DEPOSIT_BNK_CHEQUE',
  BNK_CHEQUE_REALIZE: 'REALIZE_BNK_CHEQUE',
  BNK_CHEQUE_RETURN:  'RETURN_BNK_CHEQUE',
  BNK_CHEQUE_CANCEL:  'CANCEL_BNK_CHEQUE',
  BNK_CHEQUE_PRINT:   'PRINT_BNK_CHEQUE',
  BNK_CHEQUE_EXPORT:  'EXPORT_BNK_CHEQUE',

  // ── Banking — Bank Reconciliation ────────────────────────────────────────
  BNK_RECONCILE_VIEW:     'VIEW_BNK_RECONCILE',
  BNK_RECONCILE_CREATE:   'CREATE_BNK_RECONCILE',
  BNK_RECONCILE_EDIT:     'EDIT_BNK_RECONCILE',
  BNK_RECONCILE_SUBMIT:   'SUBMIT_BNK_RECONCILE',
  BNK_RECONCILE_COMPLETE: 'COMPLETE_BNK_RECONCILE',
  BNK_RECONCILE_REOPEN:   'REOPEN_BNK_RECONCILE',
  BNK_RECONCILE_DELETE:       'DELETE_BNK_RECONCILE',
  BNK_RECONCILE_IMPORT:       'IMPORT_BNK_RECONCILE',
  BNK_RECONCILE_MATCH:        'MATCH_BNK_RECONCILE',
  BNK_RECONCILE_FORCE_MATCH:  'FORCEMATCH_BNK_RECONCILE',
  BNK_RECONCILE_OVERRIDE_DIFF:'OVERRIDEDIFF_BNK_RECONCILE',
  BNK_RECONCILE_REVERSE:      'REVERSE_BNK_RECONCILE',
  BNK_RECONCILE_PRINT:        'PRINT_BNK_RECONCILE',
  BNK_RECONCILE_EXPORT:       'EXPORT_BNK_RECONCILE',

  // ── Banking — Sensitive Overrides ─────────────────────────────────────────
  BNK_POST_LOCKED_PERIOD:          'POST_BNK_LOCKEDPERIOD',
  BNK_OVERRIDE_EXCHANGE_RATE:      'OVERRIDE_BNK_EXCHANGERATE',
  BNK_CROSS_LOCATION_POSTING:      'CROSSLOCATION_BNK_POSTING',
  BNK_REVERSE_POSTED_TRANSACTION:  'REVERSE_BNK_POSTEDTRANSACTION',

  // ── Config — Transaction Type ─────────────────────────────────────────────
  CFG_TRANSACTION_TYPE_VIEW:   'VIEW_CFG_TRANSACTIONTYPE',
  CFG_TRANSACTION_TYPE_CREATE: 'CREATE_CFG_TRANSACTIONTYPE',
  CFG_TRANSACTION_TYPE_EDIT:   'EDIT_CFG_TRANSACTIONTYPE',
  CFG_TRANSACTION_TYPE_DELETE: 'DELETE_CFG_TRANSACTIONTYPE',
  CFG_TRANSACTION_TYPE_PRINT:  'PRINT_CFG_TRANSACTIONTYPE',

  // ── Config — Document Numbering ───────────────────────────────────────────
  CFG_DOC_NUMBERING_VIEW:   'VIEW_CFG_DOCNUMBERING',
  CFG_DOC_NUMBERING_CREATE: 'CREATE_CFG_DOCNUMBERING',
  CFG_DOC_NUMBERING_EDIT:   'EDIT_CFG_DOCNUMBERING',
  CFG_DOC_NUMBERING_DELETE: 'DELETE_CFG_DOCNUMBERING',
  CFG_DOC_NUMBERING_PRINT:  'PRINT_CFG_DOCNUMBERING',

  // ── Dashboard ──────────────────────────────────────────────────────────────
  DASH_VIEW: 'VIEW_DASH_DASHBOARD',

  // ── Config — Workflow ─────────────────────────────────────────────────────
  CFG_WORKFLOW_VIEW:    'VIEW_CFG_WORKFLOW',
  CFG_WORKFLOW_CREATE:  'CREATE_CFG_WORKFLOW',
  CFG_WORKFLOW_EDIT:    'EDIT_CFG_WORKFLOW',
  CFG_WORKFLOW_DELETE:  'DELETE_CFG_WORKFLOW',
  CFG_WORKFLOW_ASSIGN:  'ASSIGN_CFG_WORKFLOW',
  CFG_WORKFLOW_MONITOR: 'MONITOR_CFG_WORKFLOW',

  // ── Document Attachments ───────────────────────────────────────────────────
  DOC_ATTACHMENT_VIEW:   'VIEW_DOC_ATTACHMENT',
  DOC_ATTACHMENT_ADD:    'ADD_DOC_ATTACHMENT',
  DOC_ATTACHMENT_DELETE: 'DELETE_DOC_ATTACHMENT',

  // ── Transaction Context Overrides ─────────────────────────────────────────
  TXN_OVERRIDE_LOCATION:    'TXN_OVERRIDE_LOCATION',
  TXN_OVERRIDE_COST_CENTER: 'TXN_OVERRIDE_COST_CENTER',
  TXN_MULTI_COST_CENTER:    'TXN_MULTI_COST_CENTER',

  // ── Transaction Visibility — cross-context inquiry ────────────────────────
  // Users without these permissions see ONLY their session-context transactions.
  TXN_VIEW_ALL_LOCATIONS:    'TXN_VIEW_ALL_LOCATIONS',
  TXN_VIEW_ALL_COST_CENTERS: 'TXN_VIEW_ALL_COST_CENTERS',
  TXN_VIEW_ALL_FY:           'TXN_VIEW_ALL_FY',
  TXN_CROSS_BRANCH_INQUIRY:  'TXN_CROSS_BRANCH_INQUIRY',

  // ── Governance — Policy Engine ─────────────────────────────────────────────
  ADMIN_POLICY_VIEW:   'VIEW_ADMIN_POLICY',
  ADMIN_POLICY_CREATE: 'CREATE_ADMIN_POLICY',
  ADMIN_POLICY_EDIT:   'EDIT_ADMIN_POLICY',
  ADMIN_POLICY_DELETE: 'DELETE_ADMIN_POLICY',

  // ── Open Item Engine ──────────────────────────────────────────────────────
  FIN_OPEN_ITEM_VIEW:        'VIEW_FIN_OPENITEM',
  FIN_OPEN_ITEM_RECALCULATE: 'RECALCULATE_FIN_OPENITEM',

  // ── AR — Invoice ──────────────────────────────────────────────────────────
  AR_INVOICE_VIEW:    'VIEW_AR_INVOICE',
  AR_INVOICE_CREATE:  'CREATE_AR_INVOICE',
  AR_INVOICE_EDIT:    'EDIT_AR_INVOICE',
  AR_INVOICE_SUBMIT:  'SUBMIT_AR_INVOICE',
  AR_INVOICE_APPROVE: 'APPROVE_AR_INVOICE',
  AR_INVOICE_REJECT:  'REJECT_AR_INVOICE',
  AR_INVOICE_DELETE:  'DELETE_AR_INVOICE',
  AR_INVOICE_POST:    'POST_AR_INVOICE',
  AR_INVOICE_CANCEL:  'CANCEL_AR_INVOICE',
  AR_INVOICE_REVERSE: 'REVERSE_AR_INVOICE',
  AR_INVOICE_PRINT:   'PRINT_AR_INVOICE',
  AR_INVOICE_EXPORT:  'EXPORT_AR_INVOICE',

  // ── AR — Customer Receipt ─────────────────────────────────────────────────
  AR_RECEIPT_VIEW:    'VIEW_AR_RECEIPT',
  AR_RECEIPT_CREATE:  'CREATE_AR_RECEIPT',
  AR_RECEIPT_EDIT:    'EDIT_AR_RECEIPT',
  AR_RECEIPT_SUBMIT:  'SUBMIT_AR_RECEIPT',
  AR_RECEIPT_APPROVE: 'APPROVE_AR_RECEIPT',
  AR_RECEIPT_REJECT:  'REJECT_AR_RECEIPT',
  AR_RECEIPT_DELETE:  'DELETE_AR_RECEIPT',
  AR_RECEIPT_POST:    'POST_AR_RECEIPT',
  AR_RECEIPT_REVERSE: 'REVERSE_AR_RECEIPT',
  AR_RECEIPT_CANCEL:  'CANCEL_AR_RECEIPT',
  AR_RECEIPT_PRINT:   'PRINT_AR_RECEIPT',
  AR_RECEIPT_EXPORT:  'EXPORT_AR_RECEIPT',

  // ── AR — Customer Allocation ──────────────────────────────────────────────
  AR_ALLOCATION_VIEW:       'VIEW_AR_ALLOCATION',
  AR_ALLOCATION_CREATE:     'CREATE_AR_ALLOCATION',
  AR_ALLOCATION_EDIT:       'EDIT_AR_ALLOCATION',
  AR_ALLOCATION_CONFIRM:    'CONFIRM_AR_ALLOCATION',
  AR_ALLOCATION_REVERSE:    'REVERSE_AR_ALLOCATION',
  AR_ALLOCATION_PRINT:      'PRINT_AR_ALLOCATION',
  AR_ALLOCATION_EXPORT:     'EXPORT_AR_ALLOCATION',
  AR_ALLOCATION_AUDIT_VIEW: 'AUDITVIEW_AR_ALLOCATION',

  // ── AP — Supplier Bill ────────────────────────────────────────────────────
  AP_BILL_VIEW:    'VIEW_AP_BILL',
  AP_BILL_CREATE:  'CREATE_AP_BILL',
  AP_BILL_EDIT:    'EDIT_AP_BILL',
  AP_BILL_SUBMIT:  'SUBMIT_AP_BILL',
  AP_BILL_APPROVE: 'APPROVE_AP_BILL',
  AP_BILL_REJECT:  'REJECT_AP_BILL',
  AP_BILL_DELETE:  'DELETE_AP_BILL',
  AP_BILL_POST:    'POST_AP_BILL',
  AP_BILL_CANCEL:  'CANCEL_AP_BILL',
  AP_BILL_REVERSE: 'REVERSE_AP_BILL',
  AP_BILL_PRINT:   'PRINT_AP_BILL',
  AP_BILL_EXPORT:  'EXPORT_AP_BILL',

  // ── AP — Supplier Payment ─────────────────────────────────────────────────
  AP_PAYMENT_VIEW:    'VIEW_AP_PAYMENT',
  AP_PAYMENT_CREATE:  'CREATE_AP_PAYMENT',
  AP_PAYMENT_EDIT:    'EDIT_AP_PAYMENT',
  /** GAP-004 — change the cheque payee away from the supplier/account default. */
  AP_PAYMENT_OVERRIDE_PAYEE: 'OVERRIDE_PAYEE_AP_PAYMENT',
  /** Raise a payment that settles no bill — an advance to a supplier (ML-07). */
  AP_PAYMENT_ON_ACCOUNT:     'RAISE_ON_ACCOUNT_AP_PAYMENT',
  /** Receive money against no invoice — an advance receipt (ML-07). */
  AR_RECEIPT_ON_ACCOUNT:     'RAISE_ON_ACCOUNT_AR_RECEIPT',
  AP_PAYMENT_SUBMIT:  'SUBMIT_AP_PAYMENT',
  AP_PAYMENT_APPROVE: 'APPROVE_AP_PAYMENT',
  AP_PAYMENT_REJECT:  'REJECT_AP_PAYMENT',
  AP_PAYMENT_DELETE:  'DELETE_AP_PAYMENT',
  AP_PAYMENT_POST:    'POST_AP_PAYMENT',
  AP_PAYMENT_CANCEL:  'CANCEL_AP_PAYMENT',
  AP_PAYMENT_REVERSE: 'REVERSE_AP_PAYMENT',
  AP_PAYMENT_PRINT:   'PRINT_AP_PAYMENT',
  AP_PAYMENT_EXPORT:  'EXPORT_AP_PAYMENT',

  // ── AP — Supplier Allocation ──────────────────────────────────────────────
  AP_ALLOCATION_VIEW:       'VIEW_AP_ALLOCATION',
  AP_ALLOCATION_CREATE:     'CREATE_AP_ALLOCATION',
  AP_ALLOCATION_EDIT:       'EDIT_AP_ALLOCATION',
  AP_ALLOCATION_CONFIRM:    'CONFIRM_AP_ALLOCATION',
  AP_ALLOCATION_REVERSE:    'REVERSE_AP_ALLOCATION',
  AP_ALLOCATION_PRINT:      'PRINT_AP_ALLOCATION',
  AP_ALLOCATION_EXPORT:     'EXPORT_AP_ALLOCATION',
  AP_ALLOCATION_AUDIT_VIEW: 'AUDITVIEW_AP_ALLOCATION',

  // ── Allocation — Sensitive Overrides ─────────────────────────────────────
  ALLOCATION_OVERRIDE_OVER_ALLOCATION:  'OVERRIDE_ALLOCATION_OVERALLOCATION',
  ALLOCATION_ALLOW_CROSS_CURRENCY:      'ALLOW_ALLOCATION_CROSSCURRENCY',
  ALLOCATION_REVERSE_CONFIRMED:         'REVERSE_ALLOCATION_CONFIRMED',
  ALLOCATION_FORCE_RECALC:              'FORCERECALC_ALLOCATION_OUTSTANDING',
  ALLOCATION_VIEW_ALL_LOCATIONS:        'VIEWALLLOCATIONS_ALLOCATION_ALLLOCATION',
  ALLOCATION_VIEW_ALL_COST_CENTERS:     'VIEWALLCOSTCENTERS_ALLOCATION_ALLCOSTCENTER',

  // ── AR — Customer Master ───────────────────────────────────────────────────
  MST_CUSTOMER_VIEW:         'VIEW_MST_CUSTOMER',
  MST_CUSTOMER_CREATE:       'CREATE_MST_CUSTOMER',
  MST_CUSTOMER_EDIT:         'EDIT_MST_CUSTOMER',
  MST_CUSTOMER_DELETE:       'DELETE_MST_CUSTOMER',
  MST_CUSTOMER_PRINT:        'PRINT_MST_CUSTOMER',
  MST_CUSTOMER_EXPORT:       'EXPORT_MST_CUSTOMER',
  MST_CUSTOMER_AUDIT_VIEW:   'AUDITVIEW_MST_CUSTOMER',
  MST_CUSTOMER_CREDIT_HOLD:  'CREDITHOLD_MST_CUSTOMER',
  MST_CUSTOMER_CREDIT_LIMIT: 'CREDITLIMIT_MST_CUSTOMER',

  // ── AP — Supplier Master ───────────────────────────────────────────────────
  MST_SUPPLIER_VIEW:          'VIEW_MST_SUPPLIER',
  MST_SUPPLIER_CREATE:        'CREATE_MST_SUPPLIER',
  MST_SUPPLIER_EDIT:          'EDIT_MST_SUPPLIER',
  MST_SUPPLIER_DELETE:        'DELETE_MST_SUPPLIER',
  MST_SUPPLIER_PRINT:         'PRINT_MST_SUPPLIER',
  MST_SUPPLIER_EXPORT:        'EXPORT_MST_SUPPLIER',
  MST_SUPPLIER_AUDIT_VIEW:    'AUDITVIEW_MST_SUPPLIER',
  MST_SUPPLIER_BANK_DETAILS:  'BANKDETAILS_MST_SUPPLIER',
  MST_SUPPLIER_PAYMENT_TERMS: 'PAYMENTTERMS_MST_SUPPLIER',

  // ── Finance — Period Closing ───────────────────────────────────────────────
  PERIOD_CLOSE_VIEW:          'VIEW_FIN_PERIODCLOSE',
  PERIOD_CLOSE_SOFT:          'SOFTCLOSE_FIN_PERIOD',
  PERIOD_CLOSE_HARD:          'HARDCLOSE_FIN_PERIOD',
  PERIOD_CLOSE_LOCK:          'LOCK_FIN_PERIOD',
  PERIOD_CLOSE_REOPEN:        'REOPEN_FIN_PERIOD',
  PERIOD_CLOSE_SUBMIT:        'SUBMIT_FIN_PERIODCLOSE',
  PERIOD_CLOSE_APPROVE:       'APPROVE_FIN_PERIODCLOSE',
  PERIOD_CLOSE_OVERRIDE_LOCK: 'OVERRIDE_LOCK_FIN_PERIOD',

  // ── Finance — Month-End Control Center ───────────────────────────────────
  MONTH_END_VIEW:          'VIEW_FIN_MONTHEND',
  MONTH_END_INITIATE:      'INITIATE_FIN_MONTHEND',
  MONTH_END_APPROVE:       'APPROVE_FIN_MONTHEND',
  MONTH_END_REOPEN:        'REOPEN_FIN_MONTHEND',
  MONTH_END_OVERRIDE_POST: 'OVERRIDEPOST_FIN_MONTHEND',
  MONTH_END_FORCE_CLOSE:   'FORCECLOSE_FIN_MONTHEND',

  // ── Finance — Year-End Close ───────────────────────────────────────────────
  YEAR_CLOSE_VIEW:    'VIEW_FIN_YEARCLOSE',
  YEAR_CLOSE_EXECUTE: 'EXECUTE_FIN_YEARCLOSE',
  YEAR_CLOSE_REOPEN:  'REOPEN_FIN_YEARCLOSE',

  // ── Finance — Recurring Journals ──────────────────────────────────────────
  RECURRING_JOURNAL_VIEW:    'VIEW_FIN_RECURRINGJOURNAL',
  RECURRING_JOURNAL_CREATE:  'CREATE_FIN_RECURRINGJOURNAL',
  RECURRING_JOURNAL_EDIT:    'EDIT_FIN_RECURRINGJOURNAL',
  RECURRING_JOURNAL_DELETE:  'DELETE_FIN_RECURRINGJOURNAL',
  RECURRING_JOURNAL_EXECUTE: 'EXECUTE_FIN_RECURRINGJOURNAL',

  // ── Finance — Accruals ────────────────────────────────────────────────────
  ACCRUAL_VIEW:    'VIEW_FIN_ACCRUAL',
  ACCRUAL_CREATE:  'CREATE_FIN_ACCRUAL',
  ACCRUAL_EDIT:    'EDIT_FIN_ACCRUAL',
  ACCRUAL_DELETE:  'DELETE_FIN_ACCRUAL',
  ACCRUAL_EXECUTE: 'EXECUTE_FIN_ACCRUAL',

  // ── Finance — GL Allocation Templates ────────────────────────────────────
  GL_ALLOCATION_VIEW:    'VIEW_FIN_GLALLOCATION',
  GL_ALLOCATION_CREATE:  'CREATE_FIN_GLALLOCATION',
  GL_ALLOCATION_EDIT:    'EDIT_FIN_GLALLOCATION',
  GL_ALLOCATION_DELETE:  'DELETE_FIN_GLALLOCATION',
  GL_ALLOCATION_EXECUTE: 'EXECUTE_FIN_GLALLOCATION',

  // ── Integration — Posting Batches ─────────────────────────────────────────
  INTEGRATION_POSTING_VIEW:     'VIEW_INT_POSTING',
  INTEGRATION_POSTING_SUBMIT:   'SUBMIT_INT_POSTING',
  INTEGRATION_POSTING_VALIDATE: 'VALIDATE_INT_POSTING',
  INTEGRATION_POSTING_POST:     'POST_INT_POSTING',
  INTEGRATION_POSTING_RETRY:    'RETRY_INT_POSTING',
  INTEGRATION_POSTING_CANCEL:   'CANCEL_INT_POSTING',
  INTEGRATION_DUPLICATE_OVERRIDE: 'OVERRIDE_INT_DUPLICATE',

  // ── Integration — Service Accounts (API Keys) ─────────────────────────────
  INTEGRATION_CLIENT_VIEW:      'VIEW_INT_CLIENT',
  INTEGRATION_CLIENT_MANAGE:    'MANAGE_INT_CLIENT',

  // ── Company onboarding (provisioning a company from a template) ───────────
  ONBOARDING_VIEW:              'VIEW_SYS_ONBOARDING',
  ONBOARDING_EXECUTE:           'EXECUTE_SYS_ONBOARDING',
  ONBOARDING_TEMPLATE_MANAGE:   'MANAGE_SYS_ONBOARDING_TEMPLATE',
  ONBOARDING_PACK_MANAGE:       'MANAGE_SYS_ONBOARDING_PACK',

  // ── Integration — Master Mapping / Masters / Settings ─────────────────────
  INTEGRATION_MAPPING_VIEW:     'VIEW_INT_MAPPING',
  INTEGRATION_MAPPING_MANAGE:   'MANAGE_INT_MAPPING',
  INTEGRATION_MAPPING_INGEST:   'INGEST_INT_MAPPING',
  INTEGRATION_MASTERS_VIEW:     'VIEW_INT_MASTERS',
  INTEGRATION_SETTINGS_VIEW:    'VIEW_INT_SETTINGS',
  INTEGRATION_SETTINGS_MANAGE:  'MANAGE_INT_SETTINGS',

  // ── Background Job Scheduler ──────────────────────────────────────────────
  SCHEDULER_VIEW:    'VIEW_SYS_SCHEDULER',
  SCHEDULER_EXECUTE: 'EXECUTE_SYS_SCHEDULER',
  SCHEDULER_CANCEL:  'CANCEL_SYS_SCHEDULER',
  SCHEDULER_RETRY:   'RETRY_SYS_SCHEDULER',
  SCHEDULER_ADMIN:   'ADMIN_SYS_SCHEDULER',

  // ── ERP Operations Dashboard ──────────────────────────────────────────────
  OPS_DASHBOARD_VIEW:     'VIEW_OPS_DASHBOARD',
  OPS_JOBS_VIEW:          'VIEW_OPS_JOBS',
  OPS_JOBS_RETRY:         'RETRY_OPS_JOBS',
  OPS_ALERTS_ACKNOWLEDGE: 'ACK_OPS_ALERTS',

  // ── Notifications ─────────────────────────────────────────────────────────
  NOTIFICATIONS_VIEW:   'VIEW_NTF_NOTIFICATION',
  NOTIFICATIONS_MANAGE: 'MANAGE_NTF_NOTIFICATION',

  // ── Card Reconciliation — Acquirers ───────────────────────────────────────
  CARD_ACQUIRER_VIEW:   'VIEW_BNK_CARDACQUIRER',
  CARD_ACQUIRER_CREATE: 'CREATE_BNK_CARDACQUIRER',
  CARD_ACQUIRER_EDIT:   'EDIT_BNK_CARDACQUIRER',
  CARD_ACQUIRER_DELETE: 'DELETE_BNK_CARDACQUIRER',

  // ── Card Reconciliation — Clearing Items ──────────────────────────────────
  CARD_CLEARING_VIEW:   'VIEW_BNK_CARDCLEARING',
  CARD_CLEARING_CREATE: 'CREATE_BNK_CARDCLEARING',
  CARD_CLEARING_EDIT:   'EDIT_BNK_CARDCLEARING',
  CARD_CLEARING_DELETE: 'DELETE_BNK_CARDCLEARING',

  // ── Card Reconciliation — Settlement Import ───────────────────────────────
  CARD_SETTLEMENT_VIEW:   'VIEW_BNK_CARDSETTLEMENT',
  CARD_SETTLEMENT_IMPORT: 'IMPORT_BNK_CARDSETTLEMENT',
  CARD_SETTLEMENT_DELETE: 'DELETE_BNK_CARDSETTLEMENT',

  // ── Card Reconciliation — Reconciliation Workspace ────────────────────────
  CARD_RECON_VIEW:        'VIEW_BNK_CARDRECON',
  CARD_RECON_CREATE:      'CREATE_BNK_CARDRECON',
  CARD_RECON_EDIT:        'EDIT_BNK_CARDRECON',
  CARD_RECON_DELETE:      'DELETE_BNK_CARDRECON',
  CARD_RECON_IMPORT:      'IMPORT_BNK_CARDRECON',
  CARD_RECON_MATCH:       'MATCH_BNK_CARDRECON',
  CARD_RECON_FORCE_MATCH: 'FORCEMATCH_BNK_CARDRECON',
  CARD_RECON_SUBMIT:      'SUBMIT_BNK_CARDRECON',
  CARD_RECON_APPROVE:     'APPROVE_BNK_CARDRECON',
  CARD_RECON_REJECT:      'REJECT_BNK_CARDRECON',
  CARD_RECON_POST:        'POST_BNK_CARDRECON',
  CARD_RECON_REVERSE:     'REVERSE_BNK_CARDRECON',
  CARD_RECON_APPROVE_VAR: 'APPROVEVAR_BNK_CARDRECON',

  // ── Card Reconciliation — Chargebacks ─────────────────────────────────────
  CARD_CHARGEBACK_VIEW:   'VIEW_BNK_CARDCHARGEBACK',
  CARD_CHARGEBACK_CREATE: 'CREATE_BNK_CARDCHARGEBACK',
  CARD_CHARGEBACK_UPDATE: 'UPDATE_BNK_CARDCHARGEBACK',
  CARD_CHARGEBACK_POST:   'POST_BNK_CARDCHARGEBACK',

  // ── Cash Denomination Configuration ────────────────────────────────────────
  CASH_DENOM_VIEW:          'VIEW_CASH_DENOM',
  CASH_DENOM_CREATE:        'CREATE_CASH_DENOM',
  CASH_DENOM_EDIT:          'EDIT_CASH_DENOM',
  CASH_DENOM_DELETE:        'DELETE_CASH_DENOM',

  // ── Payment Method Configuration ──────────────────────────────────────────
  PMT_METHOD_VIEW:          'VIEW_PMT_METHOD',
  PMT_METHOD_CREATE:        'CREATE_PMT_METHOD',
  PMT_METHOD_EDIT:          'EDIT_PMT_METHOD',
  PMT_METHOD_DELETE:        'DELETE_PMT_METHOD',

  // ── Cheque Layout Configuration ───────────────────────────────────────────
  CHEQUE_LAYOUT_VIEW:       'VIEW_CHEQUE_LAYOUT',
  // Cheque books (GAP-001) — which numbers we hold, distinct from the register's VIEW_BNK_CHEQUE.
  CHEQUE_BOOK_VIEW:         'VIEW_CFG_CHEQUEBOOK',
  CHEQUE_BOOK_CREATE:       'CREATE_CFG_CHEQUEBOOK',
  CHEQUE_BOOK_CLOSE:        'CLOSE_CFG_CHEQUEBOOK',
  CHEQUE_BOOK_VOID:         'VOID_CFG_CHEQUEBOOK',
  CHEQUE_BOOK_RELEASE:      'RELEASE_CFG_CHEQUEBOOK',
  // Ageing profiles (GAP-011) — configurable ageing bands per company + party type.
  AGEING_PROFILE_VIEW:      'VIEW_CFG_AGEING_PROFILE',
  AGEING_PROFILE_MANAGE:    'MANAGE_CFG_AGEING_PROFILE',
  // Petty cash floats (GAP-012).
  // ── Journal import (GAP-017) ──────────────────────────────────────────────
  IMP_JOURNAL_VIEW:         'VIEW_IMP_JOURNAL',
  IMP_JOURNAL_UPLOAD:       'UPLOAD_IMP_JOURNAL',
  BUD_BUDGET_VIEW:          'VIEW_BUD_BUDGET',
  BUD_BUDGET_EDIT:          'EDIT_BUD_BUDGET',
  BUD_BUDGET_APPROVE:       'APPROVE_BUD_BUDGET',
  IMP_JOURNAL_POST:         'POST_IMP_JOURNAL',

  PTY_FLOAT_VIEW:           'VIEW_PTY_FLOAT',
  PTY_FLOAT_CREATE:         'CREATE_PTY_FLOAT',
  PTY_FLOAT_TOPUP:          'TOPUP_PTY_FLOAT',
  PTY_FLOAT_PAY:            'PAY_PTY_FLOAT',
  PTY_FLOAT_RETURN:         'RETURN_PTY_FLOAT',
  PTY_FLOAT_CLOSE:          'CLOSE_PTY_FLOAT',

  // ── Petty Cash — Vouchers ──────────────────────────────────────────────────
  PTY_VOUCHER_EDIT:    'EDIT_PTY_VOUCHER',
  PTY_VOUCHER_SUBMIT:  'SUBMIT_PTY_VOUCHER',
  PTY_VOUCHER_APPROVE: 'APPROVE_PTY_VOUCHER',
  PTY_VOUCHER_REJECT:  'REJECT_PTY_VOUCHER',
  PTY_VOUCHER_POST:    'POST_PTY_VOUCHER',
  PTY_VOUCHER_CANCEL:  'CANCEL_PTY_VOUCHER',
  PTY_VOUCHER_REVERSE: 'REVERSE_PTY_VOUCHER',
  PTY_VOUCHER_PRINT:   'PRINT_PTY_VOUCHER',
  CHEQUE_LAYOUT_CREATE:     'CREATE_CHEQUE_LAYOUT',
  CHEQUE_LAYOUT_EDIT:       'EDIT_CHEQUE_LAYOUT',
  CHEQUE_LAYOUT_DELETE:     'DELETE_CHEQUE_LAYOUT',

  // ── Reconciliation Tolerance ──────────────────────────────────────────────
  RECON_TOLERANCE_VIEW:     'VIEW_RECON_TOLERANCE',
  RECON_TOLERANCE_CREATE:   'CREATE_RECON_TOLERANCE',
  RECON_TOLERANCE_EDIT:     'EDIT_RECON_TOLERANCE',
  RECON_TOLERANCE_DELETE:   'DELETE_RECON_TOLERANCE',

  // ── Cash Reconciliation ───────────────────────────────────────────────────
  CASH_RECON_VIEW:             'VIEW_CASH_RECON',
  CASH_RECON_CREATE:           'CREATE_CASH_RECON',
  CASH_RECON_EDIT:             'EDIT_CASH_RECON',
  CASH_RECON_SUBMIT:           'SUBMIT_CASH_RECON',
  CASH_RECON_APPROVE:          'APPROVE_CASH_RECON',
  CASH_RECON_APPROVE_VARIANCE: 'APPROVE_VARIANCE_CASH_RECON',
  CASH_RECON_DELETE:           'DELETE_CASH_RECON',
  CASH_RECON_PRINT:            'PRINT_CASH_RECON',

  // ── Cheque Issuance ───────────────────────────────────────────────────────
  BNK_CHEQUE_ISSUE:    'ISSUE_BNK_CHEQUE',
  BNK_CHEQUE_REPRINT:  'REPRINT_BNK_CHEQUE',
  BNK_CHEQUE_SPOIL:    'SPOIL_BNK_CHEQUE',
  // Separate from PRINT: a cheque payable to CASH can be encashed by whoever holds it, so
  // overriding the payee at print time is granted on its own (GAP-007).
  BNK_CHEQUE_CASH_PAYEE: 'OVERRIDE_CASH_PAYEE_BNK_CHEQUE',
  /** GAP-007 — change the crossing printed on a cheque. */
  BNK_CHEQUE_CROSSING:   'OVERRIDE_CROSSING_BNK_CHEQUE',

  // ── Cash Deposit Operations ───────────────────────────────────────────────
  CASH_DEPOSIT_VIEW:    'VIEW_CASH_DEPOSIT',
  CASH_DEPOSIT_CREATE:  'CREATE_CASH_DEPOSIT',
  CASH_DEPOSIT_CONFIRM: 'CONFIRM_CASH_DEPOSIT',
  CASH_DEPOSIT_DELETE:  'DELETE_CASH_DEPOSIT',
  CASH_DEPOSIT_PRINT:   'PRINT_CASH_DEPOSIT',

  // ── Tax Engine ─────────────────────────────────────────────────────────────
  TAX_JURISDICTION_VIEW:   'VIEW_TAX_JURISDICTION',
  TAX_JURISDICTION_CREATE: 'CREATE_TAX_JURISDICTION',
  TAX_JURISDICTION_EDIT:   'EDIT_TAX_JURISDICTION',
  TAX_JURISDICTION_DELETE: 'DELETE_TAX_JURISDICTION',

  TAX_TYPE_VIEW:   'VIEW_TAX_TYPE',
  TAX_TYPE_CREATE: 'CREATE_TAX_TYPE',
  TAX_TYPE_EDIT:   'EDIT_TAX_TYPE',
  TAX_TYPE_DELETE: 'DELETE_TAX_TYPE',

  TAX_CODE_VIEW:   'VIEW_TAX_CODE',
  TAX_CODE_CREATE: 'CREATE_TAX_CODE',
  TAX_CODE_EDIT:   'EDIT_TAX_CODE',
  TAX_CODE_DELETE: 'DELETE_TAX_CODE',

  TAX_GROUP_VIEW:   'VIEW_TAX_GROUP',
  TAX_GROUP_CREATE: 'CREATE_TAX_GROUP',
  TAX_GROUP_EDIT:   'EDIT_TAX_GROUP',
  TAX_GROUP_DELETE: 'DELETE_TAX_GROUP',

  TAX_WHT_RULE_VIEW:   'VIEW_TAX_WHT_RULE',
  TAX_WHT_RULE_CREATE: 'CREATE_TAX_WHT_RULE',
  TAX_WHT_RULE_EDIT:   'EDIT_TAX_WHT_RULE',
  TAX_WHT_RULE_DELETE: 'DELETE_TAX_WHT_RULE',

  TAX_REGISTRATION_VIEW:   'VIEW_TAX_REGISTRATION',
  TAX_REGISTRATION_CREATE: 'CREATE_TAX_REGISTRATION',
  TAX_REGISTRATION_EDIT:   'EDIT_TAX_REGISTRATION',
  TAX_REGISTRATION_DELETE: 'DELETE_TAX_REGISTRATION',

  TAX_INQUIRY_VIEW:      'VIEW_TAX_INQUIRY',
  TAX_REPORT_VIEW:       'VIEW_TAX_REPORT',
  TAX_OVERRIDE_APPROVE:  'APPROVE_TAX_OVERRIDE',
  TAX_EXEMPTION_APPROVE: 'APPROVE_TAX_EXEMPTION',
  TAX_WHT_PROCESS:       'PROCESS_TAX_WHT',

} as const;

export type PermissionCode = (typeof Permissions)[keyof typeof Permissions];
